import { CheckedState, LinkButtonProps, TBaseItem } from "@centricsoftware/design-system";
import { C8NodeType, getDataStoreComponents, IAggregateStoreGroupInfo, IContextualDataStoreId, IDataStoreInfo, isGlobalDataStore, queryDataStoreInfo } from "c8-platform";
import { createSelectOptionByType } from "csi/components/select/selectUtility";
import ExtendedPath from "csi/core/ExtendedPath";
import { parseExtendedPath } from "csi/core/extendedPathService";
import RuleStore from "csi/middleware/attributeRule/RuleStore";
import { getClass } from "csi/reflection/reflection";
import { getLocalizedGroupLabelWithLocaleProvider } from "csi/store/common";
import { arrayIsUnique, arrayLast } from "csi/utility/arrayUtility";
import { isEmpty, sortBy } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, SyntheticEvent } from "react";
import xmlFormat from "xml-formatter";
import AggregateStoreAlternativeStore from "./AggregateStoreAlternativeStore";
import { AggregateStoreInfoStore, IDataStoreOption } from "./AggregateStoreInfoStore";
import DataStoreDialogStore from "./DataStoreDialogStore";

const ERROR_PREFIX = "AggregateStoreError";

interface IAggregateStoreGroupValue extends IAggregateStoreGroupInfo {
	label?: string;
}

export class AggregateStoreGroupStore {
	/** reference to parent alternative */
	readonly alternative: AggregateStoreAlternativeStore;

	/** data store dialog store for new data store dialog */
	@observable.ref dataStoreDialogStore: DataStoreDialogStore;

	/** group info */
	@observable groupInfo: IAggregateStoreGroupInfo;

	/** group label in site locale */
	@observable label: string;

	/** aggregate store info store */
	get aggregateInfoStore(): AggregateStoreInfoStore {
		return this.alternative.aggregateInfoStore;
	}

	/** indicates if group can be removed from alternative */
	get canRemoveGroup(): boolean {
		const { aggregateInfoStore, alternative, hasMultipleGroups } = this;
		return alternative.dynamic || (aggregateInfoStore.overridden && hasMultipleGroups);
	}

	/** indicates if data store selection should be disabled */
	get dataStoreIdDisabled(): boolean {
		return !!this.pathError || !this.targetClass;
	}

	/** localized error message for data store id */
	get dataStoreIdError(): string | undefined {
		const { aggregateInfoStore, dataStoreIdErrorResource, groupInfo } = this;

		if (!groupInfo.dataStoreId) {
			return aggregateInfoStore.requiredValueError;
		}

		if (dataStoreIdErrorResource) {
			return aggregateInfoStore.loc.resource(`${ERROR_PREFIX}${dataStoreIdErrorResource}`);
		}
	}

	/** determine if data store is valid, and if not, then also return the error message to show */
	@computed get dataStoreIdErrorResource(): string | undefined {
		const {
			aggregateInfoStore,
			alternative,
			groupInfo,
			isDataStoreIdUnique,
			pathError,
			sourceClass: groupSourceClass,
			targetClass,
		} = this;

		if (isEmpty(groupInfo.dataStoreInfo)) {
			return "NoDataStore";
		}
		
		// assume read-only info is correct, and no reason to check data store if path is incorrect
		if (aggregateInfoStore.readOnly || pathError) {
			return;
		}
		
		const components = getDataStoreComponents(groupInfo.dataStoreId);
		if (!components) {
			return "IdFormat";
		}
		
		// if there was manual entry of the id, do not check for the correct target class as
		// an aggregateClass could exist for the store thus changing the target class of the data stores
		const { target, sourceClass } = components as IContextualDataStoreId;
		if ((target !== targetClass) && !alternative.dynamic) {
			return "Target";
		}

		// ensure source classes match - it is ok for the group's source class to be derived from the data store's source class,
		// but not the other way around. The group can use a data store that is meant for many source classes, but a group whose
		// source class is abstract cannot use a data store only meant for one concrete class
		const rcGroup = getClass(groupSourceClass);

		if (sourceClass && !rcGroup.isDerivedFrom(sourceClass)) {
			return "Source";
		}

		// for dynamic aggregate stores where data store ids are entered manually, they must be unique
		if (alternative.dynamic && !isDataStoreIdUnique) {
			return "Unique";
		}
	};

	/** link button properties for creating a new data store if allowed */
	@computed get dataStoreIdLinkButtonProps(): LinkButtonProps | undefined {
		if (!this.dataStoreIdDisabled) {
			return {
				childrenRes: { res: "AggregateStoreGroupNewDataStore" },
				onClick: this.createDataStoreDialogStore,
			};
		}
	}

	/** options for data store selection */
	@computed get dataStoreOptions(): IDataStoreOption[] {
		const { aggregateInfoStore, dataStoreOptionValue, pathError, targetClass, typedPath } = this;

		if (pathError) {
			return [];
		}

		const options = aggregateInfoStore.getDataStoreOptions(targetClass, typedPath);
		if (options) {
			return options;
		}

		return dataStoreOptionValue ? [dataStoreOptionValue] : [];
	}

	/** selected data store item */
	@computed get dataStoreOptionValue(): IDataStoreOption | undefined {
		const { aggregateInfoStore, groupInfo, hasValidDataStoreId, pathError, typedPath } = this;
		const { dataStoreId, dataStoreInfo } = groupInfo;

		if (hasValidDataStoreId && !pathError) {
			return aggregateInfoStore.createDataStoreOption(dataStoreId, dataStoreInfo, typedPath);
		}
	}
	
	/** extended path for aggregate store group; OOTB paths are not necessarily typed */
	@computed get extendedPath(): ExtendedPath | undefined {
		const { calculateExtendedPath, groupInfo } = this;
		
		if (groupInfo.path) {
			return calculateExtendedPath(groupInfo.path);
		}
	};

	/** return formatted query filter string */
	get formattedQuery(): string | undefined {
		const query = this.groupInfo.dataStoreInfo?.query;
		if (query) {
			return xmlFormat(query);
		}
	};

	/** value representing all editable settings in store */
	@computed get groupValue(): IAggregateStoreGroupValue {
		const {
			groupInfo,
			label = "",
		} = this;

		const {
			dataStoreId = "",
			dataStoreInfo = null,
			labelResource = "",
			path = "",
			suggested = false,
		} = groupInfo;
	
		return {
			dataStoreId,
			dataStoreInfo,
			label,
			labelResource,
			path,
			suggested,
		};
	}

	/** indicates if full data store info was able to be returned from server */
	get hasDataStoreInfo(): boolean {
		const { dataStoreInfo } = this.groupInfo;
		return !!dataStoreInfo?.limitPaths || !!dataStoreInfo?.query;
	}

	/** indicate if one group of many */
	get hasMultipleGroups(): boolean {
		return this.alternative.groups.length > 1;
	}

	/** indicate if valid data store id is specified */
	get hasValidDataStoreId(): boolean {
		const { dataStoreIdErrorResource, groupInfo } = this;

		// need to validate empty data store separately since an error there only arises after Save
		if (dataStoreIdErrorResource || !groupInfo.dataStoreId) {
			return false;
		}

		return !!groupInfo.dataStoreInfo;
	}

	/** return true if data store id is global */
	get isDataStoreIdGlobal(): boolean {
		return isGlobalDataStore(this.groupInfo.dataStoreId);
	}

	/** return true if data store id is unique among all other groups in the alternative */
	@computed get isDataStoreIdUnique(): boolean {
		return arrayIsUnique(this.alternative.groups, (group) => group.groupInfo.dataStoreId, this);
	}	

	/** indicates if this group is last in alternative */
	get isLastGroup(): boolean {
		return arrayLast(this.alternative.groups) === this;
	}

	/** return true if group is valid */
	get isValid(): boolean {
		const { dataStoreIdError, groupInfo, label, labelError, labelRequired, labelResource, labelResourceError, labelResourceRequired, pathError, targetClass } = this;

		return !!groupInfo.dataStoreId &&
			!dataStoreIdError &&
			(label || !labelRequired) &&
			!labelError &&
			(labelResource || !labelResourceRequired) &&
			!labelResourceError &&
			!pathError &&
			!!targetClass;
	}

	/** indicates if label field should be disabled based on state of label resource */
	get labelDisabled(): boolean {
		const { aggregateInfoStore, labelResource, labelResourceError } = this;
		return !aggregateInfoStore.readOnly && (!labelResource || !!labelResourceError);
	}

	/** error for label if not valid */
	get labelError(): string | undefined {
		const { aggregateInfoStore, label, labelDisabled, labelRequired } = this;

		if (labelRequired && !labelDisabled && !label) {
			return aggregateInfoStore.requiredValueError;
		}	
	}

	/** label placeholder for group */
	get labelPlaceholder(): string | undefined {
		const { aggregateInfoStore, groupInfo, hasMultipleGroups, isLastGroup, labelResource } = this;
		
		if (hasMultipleGroups && isLastGroup && !labelResource) {
			return getLocalizedGroupLabelWithLocaleProvider(groupInfo, aggregateInfoStore.baseConfigLocaleProvider);
		}
	}

	/** indicates if a group label is required */
	get labelRequired(): boolean {
		const { labelResource, labelResourceRequired } = this;
		return labelResourceRequired || !!labelResource;
	}

	/** label resource for group */
	get labelResource(): string | undefined {
		return this.groupInfo.labelResource;
	}

	/** error for label resource if not valid */
	get labelResourceError(): string | undefined {
		const { aggregateInfoStore, labelRequired, labelResource } = this;

		if (labelRequired && !labelResource) {
			return aggregateInfoStore.requiredValueError;
		}
	}

	/** indicates if a group label resource is required */
	get labelResourceRequired(): boolean {
		const { aggregateInfoStore, hasMultipleGroups, isLastGroup } = this;
		return aggregateInfoStore.overridden && hasMultipleGroups && !isLastGroup;
	}

	/** path for aggregate store group */
	get path(): string {
		return this.groupInfo.path;
	}

	/** error resource for path if not valid */
	get pathError(): string | undefined {
		const {
			aggregateInfoStore,
			extendedPath,
			groupInfo: { path },
		} = this;

		let err: string;

		if (aggregateInfoStore.overridden) {
			if (extendedPath) {
				if (extendedPath.startType !== aggregateInfoStore.sourceClass) {
					err = "PathStart";
				} else if (path && (extendedPath.value !== path)) {
					err = "PathTypes";
				}
			} else if (path) {
				err = "PathInvalid";
			}
		}

		if (err) {
			return `${ERROR_PREFIX}${err}`;
		}
	}

	/** only needed when refining an OOTB aggregate store or defining a new data store */
	get ruleStore(): RuleStore | undefined {
		const { aggregateInfoStore, groupInfo: { dataStoreId } } = this;

		if (dataStoreId) {
			return aggregateInfoStore.getRuleStore(dataStoreId);
		}
	}

	/** source class for group */
	get sourceClass(): C8NodeType {
		return this.typedPath?.endType || this.aggregateInfoStore.sourceClass;
	}

	/** list of suggested groups for data store select component */
	@computed get suggestedDataStoreGroups(): string[] {
		const { aggregateInfoStore, isDataStoreIdGlobal, typedPath } = this;

		if (typedPath && !isDataStoreIdGlobal) {
			const name = aggregateInfoStore.getNameForPath(typedPath);
			return [name];
		}
	}

	/** target class for group which must be concrete */
	get targetClass(): C8NodeType | undefined {
		const { aggregateInfoStore, groupInfo } = this;
		const { nodeType } = groupInfo;
		const rc = getClass(nodeType);

		// use OOTB target class if not overriding; otherwise only return target class if not abstract
		if (!aggregateInfoStore.overridden || !rc.abstract) {
			return nodeType;
		}
	}

	/** options for selecting a target class if the aggregate store target class is abstract */
	@computed get targetClassOptions(): TBaseItem[] | undefined {
		const { hasAbstractTargetClass, loc, targetClass } = this.aggregateInfoStore;
		
		if (hasAbstractTargetClass) {
			const rc = getClass(targetClass);
			const items = rc.concreteClasses.map(({ type }) => createSelectOptionByType(type, loc));
			return sortBy(items, "name");
		}
	}

	/** return typed extended path if it exists - OOTB paths may not be typed */
	get typedPath(): ExtendedPath | undefined {
		const { extendedPath } = this;
		
		if (extendedPath?.startClass) {
			return extendedPath;
		}
	}

	/** create a new data store dialog store if one doesn't already exist */
	@action createDataStoreDialogStore = (): void => {
		const {
			aggregateInfoStore,
			onCancelDataStoreDialog,
			onSaveDataStoreDialog,
			sourceClass,
			targetClass,
		} = this;

		this.dataStoreDialogStore = new DataStoreDialogStore(
			sourceClass,
			targetClass,
			aggregateInfoStore.loc,
			onSaveDataStoreDialog,
			onCancelDataStoreDialog,
		);
	};

	/** set selected data store */
	@action onChangeDataStore = async (event: SyntheticEvent, newValue: IDataStoreOption): Promise<void> => {
		if (newValue) {
			const { dataStoreId, dataStoreInfo, pathToDataStore } = newValue;
			const { groupInfo } = this;

			groupInfo.dataStoreId = dataStoreId;
			groupInfo.dataStoreInfo = dataStoreInfo;
			groupInfo.path = pathToDataStore;
		}
	};

	/** set entered data store id - used when refining OOTB aggregate store (as opposed to defining new one) */
	@action onChangeDataStoreId = async (value: string): Promise<void> => {
		const { groupInfo } = this;
		let info: IDataStoreInfo;
		let type: C8NodeType;

		try {
			// only query for data store info if the path is valid
			if (value) {
				info = await queryDataStoreInfo(value);
				type = getDataStoreComponents(value).target;
			}
		} catch (ex) {
			// empty info object means error; if null, then just not known yet
			info = {};
		}
	
		runInAction(() => {
			groupInfo.dataStoreId = value;
			groupInfo.dataStoreInfo = info;

			if (isGlobalDataStore(value)) {
				groupInfo.path = null;
			}
		});
	};

	/** set label for group which will be in site locale (similar to attribute name) */
	@action onChangeLabel = (value: string): void => {
		this.label = value;
	};

	/** set label resource for group */
	@action onChangeLabelResource = (event: ChangeEvent<HTMLInputElement>): void => {
		// NOTE: onChange is used in place of onBlurChange so that label field is disabled as soon as there
		// is some value for the resource; if you wait until blur, then the tab key will skip over the disabled
		// label field
		const { value } = event.target;
		this.groupInfo.labelResource = value;
		this.label = this.aggregateInfoStore.resolveConfigResource(value);
	};

	/** set group path */
	@action onChangePath = async (value: string): Promise<void> => {
		const {
			calculateExtendedPath,
			groupInfo,
		} = this;
		
		// correct path types with extended path if necessary
		const extendedPath = calculateExtendedPath(value);
		let path = value;

		if (extendedPath && (extendedPath.value !== groupInfo.path)) {
			// even though the user may have entered a different path than before, it's possible the extended path may have auto-corrected
			// it back to the previous value. In this case, if we reset the path to its old value, React will not update the component.
			// so in this case, keep the invalid path entered by the user
			path = extendedPath.value;
		}

		runInAction(() => {
			groupInfo.path = path;
			groupInfo.dataStoreId = undefined;
			groupInfo.dataStoreInfo = undefined;
		});
	};
	
	/** change suggested status of group */
	@action onChangeSuggested = (checked: CheckedState): void => {
		this.groupInfo.suggested = !!checked;
	};
	
	/** remove this group */
	@action onClickRemove = (): void => {
		this.aggregateInfoStore.removeGroup(this);
	};

	/** change the target class for a group (when abstract) */
	@action onChangeTargetClass = (event: SyntheticEvent, item: TBaseItem): void => {
		const { groupInfo } = this;
		groupInfo.nodeType = item.id;
		groupInfo.dataStoreId = undefined;
		groupInfo.dataStoreInfo = undefined;
	};	

	/** cancel creation of new data store */
	@action onCancelDataStoreDialog = (): void => {
		this.dataStoreDialogStore = null;
	};
	
	/** set new data store id from data store dialog */
	@action onSaveDataStoreDialog = async (): Promise<void> => {
		const {
			aggregateInfoStore,
			dataStoreDialogStore,
			onChangeDataStoreId,
		} = this;

		onChangeDataStoreId(dataStoreDialogStore.dataStoreId);
		this.dataStoreDialogStore = null;
		aggregateInfoStore.clearDataStoreOptions();
	};	

	/** load data store options if not already done */
	onOpenDataStore = (): void => {
		const { aggregateInfoStore, targetClass, typedPath } = this;
		aggregateInfoStore.queryDataStoreOptions(targetClass, typedPath);
	};

	/** determine extended path for given path */
	private calculateExtendedPath = (path: string): ExtendedPath | undefined => {
		const { overridden, sourceClass } = this.aggregateInfoStore;

		try {
			if (path) {
				const ep = parseExtendedPath(path, null, !overridden);

				if (!ep.startClass) {
					// if OOTB path is not typed, then try to create a typed path
					const typedPath = ExtendedPath.create(sourceClass, path);
					if (typedPath) {
						return typedPath;
					}
				}

				return ep;
			}
		} catch (ex) {
			// nothing
		}
	};

	constructor(groupInfo: IAggregateStoreGroupInfo, alternative: AggregateStoreAlternativeStore) {
		this.alternative = alternative;
		this.groupInfo = groupInfo;
		this.label = this.aggregateInfoStore.resolveConfigResource(groupInfo.labelResource);
		makeObservable(this);
	}
}

export default AggregateStoreGroupStore;